import { useEffect, useState, type ComponentProps } from "react";
import { Routes, createRoutesFromChildren, matchRoutes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";

/** Create the SDK wrapper after initSentry(), which runs after App's imports.
 * SDK 9 returns plain Routes if this wrapper is created before its integration
 * is initialized. Keep the component stable so navigation never remounts pages.
 */
export function SentryRoutes(props: ComponentProps<typeof Routes>) {
  const location = useLocation();
  const [InstrumentedRoutes] = useState(() =>
    Sentry.getClient()?.getIntegrationByName("BrowserTracing")
      ? Sentry.withSentryReactRouterV6Routing(Routes)
      : Routes,
  );
  const matches = matchRoutes(createRoutesFromChildren(props.children), props.location ?? location);
  // Use declared route patterns, never the URL's parameter/query values.
  const routeName = matches?.reduce((name, { route }) => {
    if (!route.path) return name;
    return route.path.startsWith("/") ? route.path : `${name.replace(/\/$/, "")}/${route.path}`;
  }, "") || "/";
  useEffect(() => {
    if (!Sentry.getClient()?.getIntegrationByName("BrowserTracing")) return;
    // SDK 9 ignores REPLACE and can retain the prior scope name when it
    // renames an unfinished navigation span. Keep error attribution current
    // for OAuth redirects and quick back/forward without creating extra spans.
    Sentry.getCurrentScope().setTransactionName(routeName);
  }, [location.key, routeName]);
  return <InstrumentedRoutes {...props} />;
}
